import { useParams } from "@tanstack/react-router";
import { Button } from "@/deps/ui-library";
import { useFundTransfer, usePostFundTransfer } from "@/api/queries";
import { ApprovalActions, ApprovalCard } from "@/components/DocumentApproval";
import { useCan } from "@/app/session";
import { DocumentScreen } from "@/components/DocumentScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

/** A transfer, and where it is posted: whoever recorded it may not post it. */
export function FundTransferDetailPage() {
  const { id } = useParams({ from: "/banking/transfers/$id" });
  const { data, isPending, error } = useFundTransfer(id);
  const post = usePostFundTransfer();
  const can = useCan();

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading transfer…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That transfer could not be loaded.</p>;

  return (
    <DocumentScreen
      title="Fund transfer"
      number={data.number}
      status={data.status}
      locked={data.status === "approved"}
      subtitle={`${data.fromName} to ${data.toName} · ${formatDate(data.date)}`}
      actions={
        data.status === "draft" ? (
          <span className="flex items-center gap-2">
            {post.isError ? (
              <span role="alert" className="text-bad-text text-xs">
                {post.error instanceof Error ? post.error.message : "That could not be posted."}
              </span>
            ) : null}
            <ApprovalActions
              kind="fundTransfer"
              id={id}
              canCreate={can("banking.transfers.create")}
              canIssue={can("banking.transfers.approve")}
              issuing={post.isPending}
              onIssue={() => post.mutate(id)}
              issueLabel="Post transfer"
              approveLabel="Approve and post"
              workingLabel="Posting"
            />
          </span>
        ) : undefined
      }
      facts={[
        { label: "From", value: data.fromName },
        { label: "To", value: data.toName },
        { label: "Date", value: formatDate(data.date) },
        { label: "Reference", value: data.reference },
        { label: "Amount", value: <span className="num">{formatAmount(data.amount)}</span> },
      ]}
      history={data.history}
      aside={<ApprovalCard kind="fundTransfer" id={id} />}
    >
      {null}
    </DocumentScreen>
  );
}
